import React from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { type SearchResultEntity, type SearchResultGraphDocument } from '@atlassian/search-client';

import { LastModified } from '../../../../../../../common/ui/last-modified';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { RestrictedLozenge } from '../../../../../../../common/ui/restricted-lozenge';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { SearchResultThumbnail } from '../../../../../../../common/ui/search-result-thumbnail';
import {
	enrichExcerpt,
	isAWSThumbnailUrlExpired,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { type useFireContextMenuButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { type useFireContextMenuCopyButtonClicked } from '../../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { type useFireResultClicked } from '../../../../../../../controllers/events/use-fire-result-clicked';
import { type useFireResultCtxMenuClicked } from '../../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { type useFireSummarizeInChatButtonClicked } from '../../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap } from '../../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../../controllers/store/bootstrap/accessors';

import { messages } from './messages';

const footerContainer = xcss({
	paddingTop: 'space.100',
});

const typeLabelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

interface GraphResultProps {
	partial: SearchResultGraphDocument<SearchResultEntity>;
	/** The position in the results list shown to the user. */
	position?: number;
	icon: React.ReactNode;
	entityType: string;
	product?: string;
	onResultClicked?: ReturnType<typeof useFireResultClicked>;
	onResultCtxMenuClicked?: ReturnType<typeof useFireResultCtxMenuClicked>;
	onSummarizeInChatButtonClicked?: ReturnType<typeof useFireSummarizeInChatButtonClicked>;
	onContextMenuCopyButtonClicked?: ReturnType<typeof useFireContextMenuCopyButtonClicked>;
	onContextMenuButtonClicked?: ReturnType<typeof useFireContextMenuButtonClicked>;
	isAllProductSelected?: boolean;
	grouping3pCohort?: string;
}

const getLabel = (entityNoun: string) => entityNoun.replace(/-/g, ' ');

export const DefaultGraphResult = ({
	icon,
	partial,
	position,
	entityType,
	product,
	onResultClicked,
	onResultCtxMenuClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	isAllProductSelected,
	grouping3pCohort,
}: GraphResultProps): React.JSX.Element => {
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	const { formatMessage } = useIntl();
	const appMode = useApplicationMode();

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');

	const { lastModifiedDate, url, permissionLevel, title, excerpt, entity } = partial;

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const resultType = partial.subtype || entityType;
	const labelMessage = messages[resultType];
	const label = resultType && labelMessage ? formatMessage(labelMessage) : getLabel(resultType);

	const isNotPublic = isNonPublic(permissionLevel);

	const headerMetadataParts = [
		['label', <Box xcss={typeLabelContainer}>{label}</Box>],
		['last-modified', lastModifiedComponent],
	];

	const hasThumbnail =
		entity &&
		'thumbnail' in entity &&
		entity.thumbnail?.externalUrl &&
		!isAWSThumbnailUrlExpired(entity.thumbnail.externalUrl);

	const isAllowedCohort = grouping3pCohort === 'enable-v2';
	const isFallbackCase =
		(grouping3pCohort === undefined || grouping3pCohort === 'control') && isAllProductSelected;
	const shouldShowLinkedResults =
		partial.linkedResults &&
		partial.linkedResults.length > 0 &&
		(isAllowedCohort || isFallbackCase);

	return (
		<SearchResult
			id={partial.id}
			product={product}
			productSelected
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={
				hasThumbnail ? (
					<SearchResultThumbnail resultUrl={url} thumbnailUrl={entity.thumbnail?.externalUrl} />
				) : (
					processExcerpt(excerpt)
				)
			}
			icon={icon}
			headerMetadataParts={headerMetadataParts}
			footer={
				<>
					{shouldShowLinkedResults ? (
						<MentionedByLinkedResults
							linkedResults={partial.linkedResults || []}
							thirdPartyConfigs={thirdPartyConfigs}
							partial={partial}
							position={position}
							url={url}
							onResultClicked={onResultClicked || (() => { })}
							onResultContextMenu={onResultCtxMenuClicked}
							isAllProductSelected={isAllProductSelected}
						/>
					) : (
						isNotPublic && (
							<Box xcss={footerContainer}>
								<RestrictedLozenge restriction={permissionLevel} />
							</Box>
						)
					)}
				</>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
			appMode={appMode}
			hasRelatedLinks={shouldShowLinkedResults || false}
		/>
	);
};
